import Seo from "@/components/feature/Seo";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import ActionButton from "@/components/base/ActionButton";
import { partners } from "@/mocks/partners";

export default function Partners() {
  return (
    <>
      <Seo
        title="Technology Partners | DJT, ZERO21 & KAL LORD'S"
        description="Meet the technology partners behind the KAL LORDS ZERO21 DJT EV retrofit collaboration — DJT, ZERO21 and KAL LORD'S and the strengths each contributes."
        keywords="EV retrofit partners, electric mobility technology, EV engineering collaboration"
        path="/partners"
      />
      <PageHero
        eyebrow="Partners"
        breadcrumb="Partners"
        title="Technology Partners in One EV Ecosystem"
        description="This initiative is presented as a technology collaboration between three partners. Each contributes a distinct strength — retrofit experience, EV engineering and charging infrastructure."
      />

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-foreground-950 text-primary-400">
                  <i className="ri-information-line text-lg" />
                </span>
                <p className="text-sm leading-relaxed text-foreground-600">
                  The partners are referenced here as technology partners within a strategic
                  collaboration. This page does not claim a merged entity, joint venture, subsidiary or
                  a single combined manufacturing capability. Capabilities are described from publicly
                  available information only.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="mt-12 space-y-6">
            {partners.map((partner, index) => (
              <Reveal key={partner.key} delay={index * 0.08}>
                <article className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  <div className="grid grid-cols-1 gap-8 p-6 md:p-9 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                      <div className="flex items-center gap-4">
                        <span className="flex h-16 w-16 items-center justify-center rounded-md border border-background-200 bg-foreground-950 font-heading text-lg font-extrabold tracking-tight text-background-50">
                          {partner.monogram}
                        </span>
                        <div>
                          <h2 className="text-2xl font-extrabold text-foreground-950">{partner.name}</h2>
                          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-700">
                            {partner.tagline}
                          </p>
                        </div>
                      </div>
                      <p className="mt-5 text-sm leading-relaxed text-foreground-600">
                        {partner.overview}
                      </p>
                      <p className="mt-3 font-label text-xs text-foreground-500">{partner.alias}</p>
                    </div>

                    <div className="lg:col-span-4">
                      <h3 className="font-label text-xs font-bold uppercase tracking-[0.16em] text-foreground-500">
                        Core Capability
                      </h3>
                      <ul className="mt-4 space-y-2.5">
                        {partner.capabilities.map((cap) => (
                          <li key={cap} className="flex items-start gap-2.5 text-sm text-foreground-800">
                            <i className="ri-check-line mt-0.5 text-primary-600" />
                            {cap}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="lg:col-span-3">
                      <h3 className="font-label text-xs font-bold uppercase tracking-[0.16em] text-foreground-500">
                        Role in the Ecosystem
                      </h3>
                      <p className="mt-4 text-sm leading-relaxed text-foreground-700">{partner.role}</p>
                      <a
                        href={partner.website}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-md border border-background-300 px-4 py-2.5 font-label text-xs font-semibold text-foreground-900 transition-colors hover:border-primary-500 hover:text-primary-700"
                      >
                        Official Website <i className="ri-external-link-line" />
                      </a>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-foreground-950 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <SectionHeading
                tone="dark"
                eyebrow="The Collaboration"
                title="Three Technology Strengths. One Electric Future."
                description="Rather than duplicating capability, the collaboration links retrofit experience, EV engineering and charging infrastructure so a retrofit can be part of a complete electric mobility ecosystem."
              />
            </Reveal>
            <Reveal delay={0.12}>
              <div className="flex flex-col gap-3 sm:flex-row">
                <ActionButton to="/technology" variant="primary" size="lg" icon="ri-cpu-line">
                  Explore Technology
                </ActionButton>
                <ActionButton to="/contact" variant="outlineLight" size="lg" icon="ri-team-line">
                  Partnership Enquiry
                </ActionButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}